<script lang="ts">
  /**
   * Pre-configured opening question + click-to-send answer chips. Each
   * chip's label becomes the customer's first message verbatim — the
   * parent (`HelpDeskWidget`) routes it through the normal `sendMessage`
   * flow so the AI replies in-thread.
   *
   * Designed to be either rendered ABOVE the FAQ list (default) or to
   * replace it entirely when `branding.welcome_chips.hide_faqs` is true.
   * That second mode is the "landing page" experience the tenant
   * configures for marketing widgets.
   */
  interface Props {
    question: string;
    options: string[];
    assistantName?: string;
    assistantAvatarUrl?: string;
    onselect: (option: string) => void;
  }

  let {
    question,
    options,
    assistantName,
    assistantAvatarUrl,
    onselect,
  }: Props = $props();

  const initial = $derived(
    (assistantName?.trim()[0] ?? 'A').toUpperCase(),
  );
</script>

<section class="hd-chips">
  <header class="hd-chips__header">
    {#if assistantAvatarUrl}
      <img
        class="hd-chips__avatar"
        src={assistantAvatarUrl}
        alt={assistantName ?? ''}
      />
    {:else}
      <span class="hd-chips__avatar hd-chips__avatar--initial" aria-hidden="true">
        {initial}
      </span>
    {/if}
    <p class="hd-chips__question">{question}</p>
  </header>

  <ul class="hd-chips__list">
    {#each options as option, i (i)}
      <li>
        <button
          type="button"
          class="hd-chips__chip"
          onclick={() => onselect(option)}
        >
          {option}
        </button>
      </li>
    {/each}
  </ul>
</section>

<style>
  .hd-chips {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 18px 20px;
    background: var(--hd-surface, #ffffff);
  }
  .hd-chips__header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  .hd-chips__avatar {
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    object-fit: cover;
    background: var(--hd-surface-muted, #f1f5f9);
  }
  .hd-chips__avatar--initial {
    display: grid;
    place-items: center;
    color: var(--hd-primary-fg, #ffffff);
    background: var(--hd-primary, #2563eb);
    font-weight: 600;
    font-size: 14px;
  }
  .hd-chips__question {
    margin: 0;
    font-size: 15px;
    line-height: 1.45;
    font-weight: 500;
    color: var(--hd-fg, #0f172a);
  }
  .hd-chips__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .hd-chips__chip {
    width: 100%;
    text-align: left;
    border: 1px solid var(--hd-border, #e2e8f0);
    background: var(--hd-surface, #ffffff);
    color: var(--hd-fg, #0f172a);
    border-radius: 12px;
    padding: 10px 14px;
    font: inherit;
    font-size: 14px;
    line-height: 1.4;
    cursor: pointer;
    transition:
      border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      background-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-chips__chip:hover {
    border-color: var(--hd-primary, #2563eb);
    background: rgb(var(--hd-primary-rgb, 37 99 235) / 0.08);
  }
  .hd-chips__chip:active {
    transform: scale(0.99);
  }
  .hd-chips__chip:focus-visible {
    outline: 2px solid var(--hd-primary, #2563eb);
    outline-offset: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    .hd-chips__chip {
      transition: none;
    }
  }
</style>
